import React, { useState } from 'react';
import {
  FiPlus, FiSave, FiTrash2, FiChevronDown, FiChevronUp, FiSkipForward, FiCopy
} from 'react-icons/fi';
import ExerciseLibraryBrowser from './ExerciseLibraryBrowser';
import ExerciseEditor from './ExerciseEditor';
import CycleRepeater from './CycleRepeater';
import './SessionBuilder.css';

const SessionBuilder = ({
  program,
  setProgram,
  currentCycleIndex,
  setCurrentCycleIndex,
  currentSessionIndex,
  setCurrentSessionIndex,
  onAutoSave
}) => {
  const [showExerciseLibrary, setShowExerciseLibrary] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [sessionName, setSessionName] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [showCycleRepeater, setShowCycleRepeater] = useState(false);

  const currentCycle = program.cycles[currentCycleIndex];
  const currentSession = currentCycle?.sessions?.[currentSessionIndex];
  const isFirstCycle = currentCycleIndex === 0;

  // Initialize new session structure
  const initializeSession = (name = '', description = '') => ({
    name: name || `Session ${currentSessionIndex + 1}`,
    description: description || '',
    blocks: []
  });

  // Add a new block
  const addBlock = (blockType) => {
    const newBlock = {
      id: `block_${Date.now()}`,
      blockType, // 'warmup', 'workout', 'cooldown'
      blockName: blockType === 'warmup' ? 'Warm-up' : blockType === 'cooldown' ? 'Cool-down' : 'Workout',
      workoutStyle: blockType === 'workout' ? 'individual' : null, // 'individual', 'superset', 'triset', 'multiset', 'cardio'
      // For circuit-style workouts (superset, triset, multiset)
      circuitRounds: 3, // Number of times to repeat the circuit
      restBetweenRounds: 90, // Rest after completing all exercises in the circuit
      exercises: []
    };

    setProgram(prev => {
      const newCycles = [...prev.cycles];
      const cycle = { ...newCycles[currentCycleIndex] };
      const sessions = [...cycle.sessions];
      const session = sessions[currentSessionIndex] || initializeSession();
      const blocks = [...session.blocks, newBlock];

      sessions[currentSessionIndex] = { ...session, blocks };
      cycle.sessions = sessions;
      newCycles[currentCycleIndex] = cycle;

      return { ...prev, cycles: newCycles };
    });

    // Auto-expand new block
    setExpandedBlocks(prev => ({ ...prev, [newBlock.id]: true }));
  };

  // Update block workout style
  const updateBlockStyle = (blockIndex, workoutStyle) => {
    setProgram(prev => {
      const newCycles = [...prev.cycles];
      const cycle = { ...newCycles[currentCycleIndex] };
      const sessions = [...cycle.sessions];
      const session = { ...sessions[currentSessionIndex] };
      const blocks = [...session.blocks];
      const block = { ...blocks[blockIndex], workoutStyle };

      blocks[blockIndex] = block;
      session.blocks = blocks;
      sessions[currentSessionIndex] = session;
      cycle.sessions = sessions;
      newCycles[currentCycleIndex] = cycle;

      return { ...prev, cycles: newCycles };
    });
  };

  // Delete block
  const deleteBlock = (blockIndex) => {
    if (window.confirm('Delete this block?')) {
      setProgram(prev => {
        const newCycles = [...prev.cycles];
        const cycle = { ...newCycles[currentCycleIndex] };
        const sessions = [...cycle.sessions];
        const session = { ...sessions[currentSessionIndex] };
        const blocks = session.blocks.filter((_, idx) => idx !== blockIndex);

        session.blocks = blocks;
        sessions[currentSessionIndex] = session;
        cycle.sessions = sessions;
        newCycles[currentCycleIndex] = cycle;

        return { ...prev, cycles: newCycles };
      });
    }
  };

  // Add exercise to block
  const addExerciseToBlock = (blockIndex, exercise) => {
    const block = currentSession?.blocks?.[blockIndex];
    const workoutStyle = block?.workoutStyle;
    const isCircuit = ['superset', 'triset', 'multiset'].includes(workoutStyle);

    // Detect if this is a cardio exercise
    const isCardio = exercise.category === 'aerobic' ||
      ['treadmill', 'stationary_bike', 'assault_bike', 'rowing_machine', 'ergo_ski',
       'elliptical', 'other_cardio', 'bike', 'pool', 'jump_rope', 'stair_climber',
       'wattbike', 'concept2_rower', 'skierg', 'versaclimber'].includes(exercise.equipment);

    const newExercise = {
      exerciseId: exercise.id,
      exerciseName: exercise.name,
      category: exercise.category,
      equipment: exercise.equipment,
      measurementType: exercise.measurementType,
      orderInCircuit: block?.exercises?.length || 0,
      primaryMetrics: isCardio ? {
        sets: workoutStyle === 'cardio' ? (exercise.defaultSets || 1) : null, // Cardio can have sets
        duration: exercise.defaultDurationMinutes || 10,
        intensity: exercise.defaultIntensity || 'moderate',
        rest: isCircuit ? null : (exercise.defaultRestSeconds || 0), // No individual rest in circuits
        notes: ''
      } : {
        sets: isCircuit ? null : (exercise.defaultSets || 3), // No individual sets in circuits
        reps: exercise.defaultReps || '10',
        rest: isCircuit ? null : (exercise.defaultRestSeconds || 60), // No individual rest in circuits
        weight: null,
        tempo: '',
        notes: ''
      }
    };

    setProgram(prev => {
      const newCycles = [...prev.cycles];
      const cycle = { ...newCycles[currentCycleIndex] };
      const sessions = [...cycle.sessions];
      const session = { ...sessions[currentSessionIndex] };
      const blocks = [...session.blocks];
      const block = { ...blocks[blockIndex] };
      const exercises = [...block.exercises, newExercise];

      block.exercises = exercises;
      blocks[blockIndex] = block;
      session.blocks = blocks;
      sessions[currentSessionIndex] = session;
      cycle.sessions = sessions;
      newCycles[currentCycleIndex] = cycle;

      return { ...prev, cycles: newCycles };
    });

    setShowExerciseLibrary(false);
  };

  // Update exercise metrics
  const updateExerciseMetrics = (blockIndex, exerciseIndex, updates) => {
    setProgram(prev => {
      const newCycles = [...prev.cycles];
      const cycle = { ...newCycles[currentCycleIndex] };
      const sessions = [...cycle.sessions];
      const session = { ...sessions[currentSessionIndex] };
      const blocks = [...session.blocks];
      const block = { ...blocks[blockIndex] };
      const exercises = [...block.exercises];
      const exercise = { ...exercises[exerciseIndex] };

      exercise.primaryMetrics = { ...exercise.primaryMetrics, ...updates };
      exercises[exerciseIndex] = exercise;
      block.exercises = exercises;
      blocks[blockIndex] = block;
      session.blocks = blocks;
      sessions[currentSessionIndex] = session;
      cycle.sessions = sessions;
      newCycles[currentCycleIndex] = cycle;

      return { ...prev, cycles: newCycles };
    });
  };

  // Delete exercise
  const deleteExercise = (blockIndex, exerciseIndex) => {
    if (window.confirm('Delete this exercise?')) {
      setProgram(prev => {
        const newCycles = [...prev.cycles];
        const cycle = { ...newCycles[currentCycleIndex] };
        const sessions = [...cycle.sessions];
        const session = { ...sessions[currentSessionIndex] };
        const blocks = [...session.blocks];
        const block = { ...blocks[blockIndex] };
        const exercises = block.exercises.filter((_, idx) => idx !== exerciseIndex);

        block.exercises = exercises;
        blocks[blockIndex] = block;
        session.blocks = blocks;
        sessions[currentSessionIndex] = session;
        cycle.sessions = sessions;
        newCycles[currentCycleIndex] = cycle;

        return { ...prev, cycles: newCycles };
      });
    }
  };

  // Save session
  const saveSession = () => {
    if (!sessionName.trim()) {
      alert('Please enter a session name');
      return;
    }

    setProgram(prev => {
      const newCycles = [...prev.cycles];
      const cycle = { ...newCycles[currentCycleIndex] };
      const sessions = [...cycle.sessions];
      const session = sessions[currentSessionIndex] || initializeSession();

      session.name = sessionName;
      session.description = sessionDescription;

      sessions[currentSessionIndex] = session;
      cycle.sessions = sessions;
      newCycles[currentCycleIndex] = cycle;

      return { ...prev, cycles: newCycles };
    });

    // Auto-save after session is saved
    if (onAutoSave) {
      onAutoSave();
    }

    // Move to next session or show cycle repeater
    const totalSessionsInCycle = program.sessionsPerCycle;
    if (currentSessionIndex < totalSessionsInCycle - 1) {
      // Move to next session in same cycle
      setCurrentSessionIndex(currentSessionIndex + 1);
      setSessionName('');
      setSessionDescription('');
    } else if (currentCycleIndex < program.numberOfCycles - 1) {
      // Completed current cycle, move to next cycle
      setCurrentCycleIndex(currentCycleIndex + 1);
      setCurrentSessionIndex(0);
      setSessionName('');
      setSessionDescription('');
      // Show repeater to copy from previous cycle
      setShowCycleRepeater(true);
    } else {
      // Completed all cycles
      alert('All cycles complete!');
    }
  };

  // Skip session
  const skipSession = () => {
    const totalSessionsInCycle = program.sessionsPerCycle;
    if (currentSessionIndex < totalSessionsInCycle - 1) {
      setCurrentSessionIndex(currentSessionIndex + 1);
    } else if (currentCycleIndex < program.numberOfCycles - 1) {
      setCurrentCycleIndex(currentCycleIndex + 1);
      setCurrentSessionIndex(0);
    }
  };

  // Toggle block expansion
  const toggleBlockExpansion = (blockId) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  };

  // Load session data when switching sessions (not when currentSession object changes)
  React.useEffect(() => {
    const cycle = program.cycles[currentCycleIndex];
    const session = cycle?.sessions?.[currentSessionIndex];

    if (session) {
      setSessionName(session.name || '');
      setSessionDescription(session.description || '');
    } else {
      setSessionName('');
      setSessionDescription('');
    }
  }, [currentCycleIndex, currentSessionIndex]); // Only re-run when switching sessions, not when session data changes

  if (showCycleRepeater) {
    return (
      <CycleRepeater
        program={program}
        setProgram={setProgram}
        sourceCycleIndex={currentCycleIndex - 1}
        targetCycleIndex={currentCycleIndex}
        onComplete={() => {
          setShowCycleRepeater(false);
          setCurrentSessionIndex(0);
        }}
        onSkip={() => {
          setShowCycleRepeater(false);
          setCurrentSessionIndex(0);
        }}
      />
    );
  }

  return (
    <div className="session-builder">
      <div className="session-header">
        <h3>
          Cycle {currentCycleIndex + 1} of {program.numberOfCycles} -
          Session {currentSessionIndex + 1} of {program.sessionsPerCycle}
        </h3>
      </div>

      <div className="session-form">
        <div className="form-group">
          <label>Session Name *</label>
          <input
            type="text"
            placeholder="e.g., Upper Body Push, Lower Body Pull"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Session Description (Optional)</label>
          <textarea
            placeholder="Add notes about this session..."
            value={sessionDescription}
            onChange={(e) => setSessionDescription(e.target.value)}
            rows="2"
          />
        </div>

        {/* Add Block Buttons */}
        <div className="add-block-section">
          <h4>Add Block</h4>
          <div className="block-type-buttons">
            <button onClick={() => addBlock('warmup')} className="block-btn warmup">
              <FiPlus /> Warm-up
            </button>
            <button onClick={() => addBlock('workout')} className="block-btn workout">
              <FiPlus /> Workout
            </button>
            <button onClick={() => addBlock('cooldown')} className="block-btn cooldown">
              <FiPlus /> Cool-down
            </button>
          </div>
        </div>

        {/* Blocks */}
        <div className="session-blocks">
          {currentSession?.blocks?.map((block, blockIndex) => (
            <div key={block.id} className="block-container">
              <div className="block-header">
                <div className="block-header-left">
                  <button onClick={() => toggleBlockExpansion(block.id)} className="block-toggle">
                    {expandedBlocks[block.id] ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                  <input
                    type="text"
                    className="block-name-input"
                    value={block.blockName}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setProgram(prev => {
                        const newCycles = [...prev.cycles];
                        const cycle = { ...newCycles[currentCycleIndex] };
                        const sessions = [...cycle.sessions];
                        const session = { ...sessions[currentSessionIndex] };
                        const blocks = [...session.blocks];
                        blocks[blockIndex] = { ...blocks[blockIndex], blockName: newName };
                        session.blocks = blocks;
                        sessions[currentSessionIndex] = session;
                        cycle.sessions = sessions;
                        newCycles[currentCycleIndex] = cycle;
                        return { ...prev, cycles: newCycles };
                      });
                    }}
                  />
                  {block.blockType === 'workout' && (
                    <select
                      className="workout-style-select"
                      value={block.workoutStyle}
                      onChange={(e) => updateBlockStyle(blockIndex, e.target.value)}
                    >
                      <option value="individual">Individual Exercises</option>
                      <option value="superset">Superset (2 exercises)</option>
                      <option value="triset">Triset (3 exercises)</option>
                      <option value="multiset">Multiset (4+ exercises)</option>
                      <option value="cardio">Cardio</option>
                    </select>
                  )}
                  <span className="exercise-count">{block.exercises?.length || 0} exercises</span>
                </div>
                <div className="block-actions">
                  <button
                    className="add-exercise-btn"
                    onClick={() => {
                      setSelectedBlock(blockIndex);
                      setShowExerciseLibrary(true);
                    }}
                  >
                    <FiPlus /> Add Exercise
                  </button>
                  <button className="delete-block-btn" onClick={() => deleteBlock(blockIndex)}>
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              {expandedBlocks[block.id] !== false && (
                <>
                  {/* Circuit Visual Layout (Superset, Triset, Multiset) */}
                  {['superset', 'triset', 'multiset'].includes(block.workoutStyle) ? (
                    <div className="circuit-visual-container">
                      <div className="circuit-bracket-group">
                        {/* Left Bracket */}
                        <div className="circuit-bracket"></div>

                        {/* Exercise Slots */}
                        <div className="circuit-exercises-group">
                          {(() => {
                            let maxExercises;
                            if (block.workoutStyle === 'superset') {
                              maxExercises = 2;
                            } else if (block.workoutStyle === 'triset') {
                              maxExercises = 3;
                            } else {
                              // Multiset: show filled slots + 1 empty slot
                              maxExercises = (block.exercises?.length || 0) > 0 ?
                                              block.exercises.length : 4;
                            }
                            const slots = [];

                            for (let i = 0; i < maxExercises; i++) {
                              const exercise = block.exercises?.[i];
                              slots.push(
                                <div key={i} className="circuit-exercise-slot">
                                  {exercise ? (
                                    <div className="circuit-exercise-row">
                                      <div className="circuit-exercise-info">
                                        <span className="circuit-exercise-name">{exercise.exerciseName}</span>
                                        <span className="circuit-exercise-details">
                                          {exercise.primaryMetrics?.reps || '10'} reps
                                          {exercise.primaryMetrics?.weight && ` @ ${exercise.primaryMetrics.weight}kg`}
                                        </span>
                                      </div>
                                      <button
                                        className="circuit-exercise-edit"
                                        onClick={() => {
                                          // Toggle expanded state for editing
                                          const exerciseEl = document.getElementById(`circuit-ex-${blockIndex}-${i}`);
                                          if (exerciseEl) exerciseEl.classList.toggle('expanded');
                                        }}
                                      >
                                        Edit Details
                                      </button>
                                      <button
                                        className="circuit-exercise-delete"
                                        onClick={() => deleteExercise(blockIndex, i)}
                                      >
                                        <FiTrash2 />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      className="circuit-add-exercise-slot"
                                      onClick={() => {
                                        setSelectedBlock(blockIndex);
                                        setShowExerciseLibrary(true);
                                      }}
                                    >
                                      <FiPlus /> Add Exercise {i + 1}
                                    </button>
                                  )}

                                  {/* Expandable exercise editor */}
                                  {exercise && (
                                    <div id={`circuit-ex-${blockIndex}-${i}`} className="circuit-exercise-expanded">
                                      <ExerciseEditor
                                        exercise={exercise}
                                        blockType={block.workoutStyle}
                                        onUpdate={(updates) => updateExerciseMetrics(blockIndex, i, updates)}
                                        onDelete={() => deleteExercise(blockIndex, i)}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return slots;
                          })()}

                          {/* Add More Exercises (for Multiset only) */}
                          {block.workoutStyle === 'multiset' && (
                            <button
                              className="circuit-add-more-btn"
                              onClick={() => {
                                setSelectedBlock(blockIndex);
                                setShowExerciseLibrary(true);
                              }}
                            >
                              <FiPlus /> Add Another Exercise
                            </button>
                          )}

                          {/* Rest Interval */}
                          <div className="circuit-rest-slot">
                            <label>Rest Between Rounds</label>
                            <div className="circuit-rest-control">
                              <input
                                type="number"
                                min="0"
                                max="300"
                                step="15"
                                value={block.restBetweenRounds || 90}
                                onChange={(e) => {
                                  const rest = parseInt(e.target.value);
                                  setProgram(prev => {
                                    const newCycles = [...prev.cycles];
                                    const cycle = { ...newCycles[currentCycleIndex] };
                                    const sessions = [...cycle.sessions];
                                    const session = { ...sessions[currentSessionIndex] };
                                    const blocks = [...session.blocks];
                                    blocks[blockIndex] = { ...blocks[blockIndex], restBetweenRounds: rest };
                                    session.blocks = blocks;
                                    sessions[currentSessionIndex] = session;
                                    cycle.sessions = sessions;
                                    newCycles[currentCycleIndex] = cycle;
                                    return { ...prev, cycles: newCycles };
                                  });
                                }}
                              />
                              <span>seconds</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Rounds/Sets Control */}
                      <div className="circuit-rounds-control">
                        <label>Rounds</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={block.circuitRounds || 3}
                          onChange={(e) => {
                            const rounds = parseInt(e.target.value);
                            setProgram(prev => {
                              const newCycles = [...prev.cycles];
                              const cycle = { ...newCycles[currentCycleIndex] };
                              const sessions = [...cycle.sessions];
                              const session = { ...sessions[currentSessionIndex] };
                              const blocks = [...session.blocks];
                              blocks[blockIndex] = { ...blocks[blockIndex], circuitRounds: rounds };
                              session.blocks = blocks;
                              sessions[currentSessionIndex] = session;
                              cycle.sessions = sessions;
                              newCycles[currentCycleIndex] = cycle;
                              return { ...prev, cycles: newCycles };
                            });
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    /* Individual / Cardio Layout */
                    <div className="block-exercises">
                      {(!block.exercises || block.exercises.length === 0) ? (
                        <div className="no-exercises">
                          <p>No exercises added yet</p>
                          <button
                            onClick={() => {
                              setSelectedBlock(blockIndex);
                              setShowExerciseLibrary(true);
                            }}
                          >
                            <FiPlus /> Add Exercise
                          </button>
                        </div>
                      ) : (
                        block.exercises.map((exercise, exerciseIndex) => (
                          <ExerciseEditor
                            key={exerciseIndex}
                            exercise={exercise}
                            blockType={block.workoutStyle}
                            onUpdate={(updates) => updateExerciseMetrics(blockIndex, exerciseIndex, updates)}
                            onDelete={() => deleteExercise(blockIndex, exerciseIndex)}
                          />
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Session Actions */}
        <div className="session-actions">
          <button className="skip-session-btn" onClick={skipSession}>
            <FiSkipForward /> Skip Session
          </button>
          <button className="save-session-btn" onClick={saveSession}>
            <FiSave /> Save & Continue
          </button>
        </div>
      </div>

      {/* Exercise Library Modal */}
      {showExerciseLibrary && (
        <ExerciseLibraryBrowser
          onSelect={(exercise) => addExerciseToBlock(selectedBlock, exercise)}
          onClose={() => setShowExerciseLibrary(false)}
        />
      )}
    </div>
  );
};

export default SessionBuilder;
